import { FunctionComponent, useEffect, useState } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import MyLocationIcon from '@mui/icons-material/MyLocation';
import CircularProgress from '@mui/material/CircularProgress';
import styles from './ProviderMap.module.css';

// Fix for default markers in React Leaflet
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: '/images/marker-icon-2x.png',
  iconUrl: '/images/marker-icon.png',
  shadowUrl: '/images/marker-shadow.png',
});

interface Provider {
  id: string;
  name: string;
  specialty: string;
  address: string;
  distance: string;
  lat: number;
  lng: number;
}

interface ProviderMapProps {
  providers?: Provider[];
  height?: string;
}

// Component to handle map centering
const MapController: FunctionComponent<{ center: [number, number]; shouldRecenter: boolean }> = ({ center, shouldRecenter }) => {
  const map = useMap();
  
  useEffect(() => {
    if (shouldRecenter) {
      map.setView(center, 13);
    }
  }, [center, map, shouldRecenter]);
  
  return null;
};

const ProviderMap: FunctionComponent<ProviderMapProps> = ({ providers = [], height = '300px' }) => {
  const [userLocation, setUserLocation] = useState<[number, number] | null>(null);
  const [isLoadingLocation, setIsLoadingLocation] = useState(true);
  const [shouldRecenter, setShouldRecenter] = useState(false);
  
  // Default to San Francisco
  const defaultCenter: [number, number] = [37.7749, -122.4194];
  const mapCenter = userLocation || defaultCenter;

  // Mock providers if none provided
  const mockProviders: Provider[] = providers.length > 0 ? providers : [
    {
      id: '1',
      name: 'Dr. Emily Carter',
      specialty: 'Internal Medicine',
      address: '123 Medical Center Dr, San Francisco, CA',
      distance: '1.2 miles',
      lat: 37.7849,
      lng: -122.4094
    },
    {
      id: '2',
      name: 'Dr. David Lee',
      specialty: 'Cardiology',
      address: '456 Heart Health Plaza, San Francisco, CA',
      distance: '2.5 miles',
      lat: 37.7649,
      lng: -122.4294
    },
    {
      id: '3',
      name: 'Dr. Sarah Jones',
      specialty: 'Pediatrics',
      address: '789 Children\'s Way, San Francisco, CA',
      distance: '3.1 miles',
      lat: 37.7749,
      lng: -122.4394
    },
    {
      id: '4',
      name: 'Dr. Michael Chen',
      specialty: 'Orthopedic Surgery',
      address: '321 Sports Medicine Blvd, San Francisco, CA',
      distance: '4.8 miles',
      lat: 37.7949,
      lng: -122.4094
    }
  ];

  useEffect(() => {
    // Try to get user's location
    if ('geolocation' in navigator) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords;
          setUserLocation([latitude, longitude]);
          setIsLoadingLocation(false);
        },
        (error) => {
          console.log('Location error:', error);
          setIsLoadingLocation(false);
        },
        {
          enableHighAccuracy: false,
          timeout: 5000,
          maximumAge: 300000 // 5 minutes
        }
      );
    } else {
      setIsLoadingLocation(false);
    }
  }, []);

  // Create custom icons
  const providerIcon = new L.Icon({
    iconUrl: '/images/provider-marker.svg',
    iconSize: [32, 32],
    iconAnchor: [16, 16],
    popupAnchor: [0, -16],
  });

  const userIcon = new L.Icon({
    iconUrl: '/images/user-location.svg',
    iconSize: [24, 24],
    iconAnchor: [12, 12],
    popupAnchor: [0, -12],
  });

  return (
    <div className={styles.mapContainer} style={{ height }}>
      {isLoadingLocation && (
        <div className={styles.loadingOverlay}>
          <CircularProgress size={48} style={{ color: 'var(--color-brand-600)' }} />
          <p>Finding your location...</p>
        </div>
      )}
      
      <MapContainer
        center={mapCenter}
        zoom={13}
        className={styles.map}
        scrollWheelZoom={false}
      >
        <MapController center={mapCenter} shouldRecenter={shouldRecenter} />
        
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>'
          url="https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png"
        />
        
        {/* User location marker */}
        {userLocation && (
          <Marker position={userLocation} icon={userIcon}>
            <Popup>
              <div className={styles.popup}>
                <strong>Your Location</strong>
              </div>
            </Popup>
          </Marker>
        )}
        
        {/* Provider markers */}
        {mockProviders.map((provider) => (
          <Marker
            key={provider.id}
            position={[provider.lat, provider.lng]}
            icon={providerIcon}
          >
            <Popup>
              <div className={styles.popup}>
                <h3 className={styles.providerName}>{provider.name}</h3>
                <p className={styles.providerSpecialty}>{provider.specialty}</p>
                <p className={styles.providerAddress}>{provider.address}</p>
                <p className={styles.providerDistance}>
                  <LocationOnIcon style={{ fontSize: '16px', color: 'var(--color-brand-600)', verticalAlign: 'middle', marginRight: '4px' }} />
                  {provider.distance}
                </p>
                <button className={styles.viewButton}>View Details</button>
              </div>
            </Popup>
          </Marker>
        ))}
      </MapContainer>
      
      <div className={styles.mapControls}>
        <button 
          className={styles.locationButton}
          onClick={() => {
            if (userLocation) {
              // Trigger map recentering
              setShouldRecenter(true);
              // Reset the flag after a short delay
              setTimeout(() => setShouldRecenter(false), 100);
            }
          }}
          disabled={!userLocation}
        >
          <MyLocationIcon style={{ fontSize: '18px', marginRight: '6px', verticalAlign: 'middle' }} />
          My Location
        </button>
      </div>
    </div>
  );
};

export default ProviderMap;
